import { useEffect, useState } from "react";
import { api } from "../lib/api";

export type Preset = {
  key: string;
  label: string;
  blurb: string;
  age: number;
  capabilities: Record<string, boolean>;
};

export type SpecialPolicyOption = { key: string; label: string; blurb: string };

export type PresetCatalogue = {
  presets: Preset[];
  capabilities: { key: string; fromAge: number }[];
  specialPolicies: SpecialPolicyOption[];
};

/* Friendly names for the capability flags.
 *
 * The stored keys are engineering vocabulary; a parent should read what their
 * child will actually be able to do. The server owns WHICH age each flag
 * belongs to -- that table has to match the defaults it applies -- and this
 * file owns the wording, which is copy.
 */
const LABELS: Record<string, { label: string; hint?: string }> = {
  can_view_own_balance: { label: "See their own money" },
  can_claim_chores: { label: "Check off their own jobs" },
  sees_bucket_icons: { label: "See the jar pictures" },
  sees_total_only: {
    label: "Show one total instead of three jars",
    hint: "Simpler for the youngest. Turn off when they can handle three.",
  },
  can_request_cashout: { label: "Ask for real cash" },
  can_log_spending: { label: "Write down what they spent" },
  sees_interest: { label: "See interest they earned" },
  can_set_goal: { label: "Set their own saving goal" },
  sees_transaction_history: { label: "See their history" },
  sees_percentages: {
    label: "See the percentages",
    hint: "Percentages are abstract before about seven.",
  },
  sees_running_balance: { label: "See a running balance" },
  sees_interest_rate_math: { label: "See how interest is worked out" },
  can_edit_goal: { label: "Change their own goal" },
  can_transfer_between_buckets: { label: "Move money between their own jars" },
  sees_projections: { label: "See what they'll have later" },
};

export function usePresets(): PresetCatalogue | null {
  const [catalogue, setCatalogue] = useState<PresetCatalogue | null>(null);
  useEffect(() => {
    api.get<PresetCatalogue>("/capability-presets").then(setCatalogue).catch(() => {});
  }, []);
  return catalogue;
}

/* The age groups, as a row of cards.
 *
 * A starting point, not a setting: whichever group is picked, every switch
 * underneath stays editable. Saying so on the card is what stops a parent
 * hunting for the "custom" option that does not need to exist.
 */
export function PresetPicker({
  presets,
  value,
  onChange,
  disabled,
}: {
  presets: Preset[];
  value: string;
  onChange: (key: string) => void;
  disabled?: boolean;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Starting point"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
        gap: 8,
      }}
    >
      {presets.map((p) => {
        const on = p.key === value;
        return (
          <button
            key={p.key}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={disabled}
            onClick={() => onChange(p.key)}
            style={{
              display: "block",
              textAlign: "left",
              minHeight: 0,
              padding: "10px 12px",
              background: on ? "var(--ink)" : "var(--paper)",
              color: on ? "var(--paper)" : "var(--ink)",
              borderColor: on ? "var(--ink)" : "var(--paper-edge)",
              boxShadow: on ? "0 3px 0 var(--kraft-shadow)" : "none",
            }}
          >
            <span
              style={{
                display: "block",
                fontFamily: "var(--font-display)",
                fontSize: 17,
                lineHeight: 1.2,
              }}
            >
              {p.label}
            </span>
            <span
              style={{
                display: "block",
                fontSize: 12.5,
                marginTop: 3,
                color: on ? "var(--kraft)" : "var(--ink-3)",
              }}
            >
              {p.blurb}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function groupHeading(age: number): string {
  return age <= 4 ? "The basics" : `Usually around ${age}`;
}

/* Every switch, grouped by the age it belongs to.
 *
 * Grouping is the whole point: fifteen flat checkboxes is a wall, and a parent
 * deciding "is my six-year-old ready for this?" is thinking in ages, not in
 * features. Rows that no longer match the chosen group are marked, so a parent
 * can see at a glance what they have changed by hand.
 */
export function CapabilityList({
  catalogue,
  capabilities,
  presetKey,
  busyKey,
  onToggle,
}: {
  catalogue: PresetCatalogue;
  capabilities: Record<string, boolean>;
  presetKey: string;
  busyKey?: string | null;
  onToggle: (key: string, next: boolean) => void;
}) {
  const preset = catalogue.presets.find((p) => p.key === presetKey);
  // 999 marks a v1 placeholder. A toggle that does nothing is worse than no
  // toggle, so those never reach the page.
  const rows = catalogue.capabilities.filter(
    (c) => c.fromAge < 900 && LABELS[c.key],
  );
  const ages = [...new Set(rows.map((c) => c.fromAge))].sort((a, b) => a - b);

  return (
    <div style={{ display: "grid", gap: 18 }}>
      {ages.map((age) => (
        <div key={age}>
          <h3
            style={{
              fontSize: 13,
              textTransform: "uppercase",
              letterSpacing: "0.07em",
              color: "var(--ink-3)",
              marginBottom: 2,
            }}
          >
            {groupHeading(age)}
          </h3>
          {rows
            .filter((c) => c.fromAge === age)
            .map((c) => {
              const on = Boolean(capabilities[c.key]);
              const changed = preset && Boolean(preset.capabilities[c.key]) !== on;
              return (
                <label
                  key={c.key}
                  style={{
                    display: "flex",
                    gap: 12,
                    alignItems: "flex-start",
                    padding: "10px 4px",
                    cursor: "pointer",
                    borderBottom: "1px solid var(--paper-edge)",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={busyKey === c.key}
                    onChange={() => onToggle(c.key, !on)}
                    style={{
                      width: 22,
                      height: 22,
                      minHeight: 0,
                      flex: "0 0 auto",
                      marginTop: 2,
                    }}
                  />
                  <span style={{ flex: 1 }}>
                    <span style={{ display: "block", fontSize: 15 }}>
                      {LABELS[c.key].label}
                    </span>
                    {LABELS[c.key].hint && (
                      <span style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
                        {LABELS[c.key].hint}
                      </span>
                    )}
                  </span>
                  {changed && (
                    <span
                      style={{
                        flex: "0 0 auto",
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        color: "var(--ink-3)",
                        border: "1.5px solid var(--paper-edge)",
                        borderRadius: 999,
                        padding: "2px 8px",
                        marginTop: 3,
                      }}
                    >
                      changed
                    </span>
                  )}
                </label>
              );
            })}
        </div>
      ))}
    </div>
  );
}


/* Who may take money back OUT of the Special jar.
 *
 * Three states rather than two, so it is a list rather than a switch, and it
 * sits apart from the age groups above because it is the one rule a parent is
 * likely to move as a child earns trust rather than as they get older.
 */
export function SpecialPolicyPicker({
  options,
  value,
  onChange,
  disabled,
}: {
  options: SpecialPolicyOption[];
  value: string;
  onChange: (key: string) => void;
  disabled?: boolean;
}) {
  return (
    <div role="radiogroup" aria-label="Taking money out of Special"
         style={{ display: "grid", gap: 8 }}>
      {options.map((o) => {
        const on = o.key === value;
        return (
          <button
            key={o.key}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={disabled}
            onClick={() => onChange(o.key)}
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 10,
              textAlign: "left",
              minHeight: 0,
              padding: "11px 13px",
              background: on ? "var(--special-soft)" : "var(--paper)",
              borderColor: on ? "var(--special)" : "var(--paper-edge)",
              borderWidth: on ? 3 : 2,
              boxShadow: "none",
            }}
          >
            <span style={{ fontWeight: 600, flex: "0 0 auto" }}>{o.label}</span>
            <span style={{ fontSize: 12.5, color: "var(--ink-3)" }}>{o.blurb}</span>
          </button>
        );
      })}
    </div>
  );
}
